import { z } from "zod";

import { fetchWithAuth } from "@/lib/api";
import { parseApiResponse } from "@/lib/api-validation";

import { API_BASE } from "@/config/api";

const portfolioPositionsPreferenceSchema = z.object({
  version: z.number().optional(),
  column_order: z.array(z.string()),
  visibility: z.record(z.string(), z.boolean()),
  updated_at: z.string().optional(),
});

const getResponseSchema = z.object({
  preference: portfolioPositionsPreferenceSchema.nullable(),
});

const putResponseSchema = z.object({
  preference: portfolioPositionsPreferenceSchema.optional(),
  ok: z.boolean().optional(),
});

export type PortfolioPositionsTablePreferenceDTO = z.infer<
  typeof portfolioPositionsPreferenceSchema
>;

export async function getPortfolioPositionsTablePreference(): Promise<PortfolioPositionsTablePreferenceDTO | null> {
  const res = await fetchWithAuth(
    `${API_BASE}/users/me/ui-preferences/portfolio-positions-table`,
  );
  if (!res.ok) {
    throw new Error("Failed to load column preferences");
  }
  const data = await res.json();
  const parsed = parseApiResponse(
    getResponseSchema,
    data,
    "getPortfolioPositionsTablePreference",
  );
  return parsed.preference;
}

export async function putPortfolioPositionsTablePreference(
  body: Pick<PortfolioPositionsTablePreferenceDTO, "column_order" | "visibility">,
): Promise<PortfolioPositionsTablePreferenceDTO | null> {
  const res = await fetchWithAuth(
    `${API_BASE}/users/me/ui-preferences/portfolio-positions-table`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        column_order: body.column_order,
        visibility: body.visibility,
      }),
    },
  );
  if (!res.ok) {
    throw new Error("Failed to save column preferences");
  }
  const data = await res.json();
  const parsed = parseApiResponse(
    putResponseSchema,
    data,
    "putPortfolioPositionsTablePreference",
  );
  return parsed.preference ?? null;
}
